/**
 * Pure derivation of what the Actions tab shows and counts. Moved out of
 * ControlCenter/tabs/ActionsTab.tsx (track:attention-dot-one-count, spec
 * #49392) so the sidebar dot, the tab-bar badge and the tab share one
 * definition without the sidebar importing the tab's UI into the main bundle.
 * Type-only imports from RelayDataProvider are erased at build.
 */
import type { ActiveSprintInfo, ModalQueueItem } from "./RelayDataProvider";
import type { TeamLogMessage } from "../types";
import { TAG, replyToSeqs } from "./teamLogTags";

/** Roster of human authors: email local-part, trimmed and lowercased. */
export function humanRosterAuthorsFromMembers(members: { email?: string | null }[]): Set<string> {
  return new Set(
    members
      .map(m => m.email?.trim().toLowerCase().split("@")[0])
      .filter((s): s is string => !!s),
  );
}

// Every tag this tab's derivations key off of (kind tags + resolution/reply
// tags) — one array-contains-any query (limit 30), one listener. "blocked"
// is fetched only to recognize the daemon's ship-evidence hold (see
// isShipEvidenceHold); no other blocked post produces a card.
export const ACTIONS_TAB_TAGS = [
  "track-stopped",
  "build-stopped",
  "action:requeue",
  "action:requeue-with-note",
  "action:dismiss-stopped",
  "critical-authorized",
  "authorization-dismissed",
  "agent-died",
  "finding-gate-held",
  "watchdog",
  "scheduler-alert",
  "stale-agent-warning",
  "stale-agent-warning-resolved",
  "resolved",
  "blocker-resolved",
  "finding-acknowledged",
  "blocked",
];

export interface SprintItem {
  title: string;
  blast?: string;
  status?: string;
}

export function trackFromTags(tags: string[] | undefined): string | undefined {
  const tag = (tags ?? []).find(t => t.startsWith("track:"));
  return tag ? tag.slice("track:".length) : undefined;
}

export function findRawTag(tags: string[] | undefined, prefix: string): string | undefined {
  return (tags ?? []).find(t => t.startsWith(prefix));
}

function replyToSeq(tags: string[] | undefined): number | undefined {
  const tag = findRawTag(tags, "reply-to:");
  if (!tag) return undefined;
  const n = parseInt(tag.slice("reply-to:".length), 10);
  return Number.isNaN(n) ? undefined : n;
}

// track:track-endings-and-requeue (spec #47964, amendment 1 item 1;
// decision #48177) — a stopped track (track-stopped: reviewer disagreed
// twice; build-stopped: the builder itself needs a human's help) is a
// legitimate human decision (re-queue or leave it), so it must appear on
// the Actions tab. Decision #48177's "appears only when a human must act"
// means: once a track's most recent track-tagged message is a stop tag,
// AND no qualifying action:requeue/action:requeue-with-note has been
// posted for it since, it needs a decision — the mirror of the
// scheduler's own _has_stopped_track_ack (deterministic_scheduler.py).
//
// advisory #48199 item 4 (closing backlog #48196): originally this list
// counted ANY action:requeue/action:requeue-with-note as resolving,
// regardless of author — unlike the backend's own gate, which only
// honors an ack from a confirmed human_lead/human_partner roster member
// (_get_human_roster_authors). An agent can technically post
// action:requeue today (no §30-A-level enforcement stops it), and when
// one did, the card would disappear even though the scheduler correctly
// ignored the ack and the track stayed un-requeued — decision #48177
// rule 4 ("when a human acts on an item, that item leaves the tab") read
// backwards: the item left the tab when a human had NOT acted. Fixed:
// humanRosterAuthors, when provided, is checked the same way the
// backend does. Left optional (not required) so computeTotalActions'
// existing 2-arg call sites — including the nav-badge listener in
// ControlCenter/index.tsx, which has no roster fetch of its own and
// isn't a decision surface with a button, just a count — keep compiling
// and keep their prior (slightly coarser, unaffected by this fix)
// behavior; the ActionsTab component itself always passes the real
// roster (see its own useWorkspaceMembers call) for the card that
// actually renders buttons.
export function collectStoppedTracksNeedingRequeue(teamLog: TeamLogMessage[], humanRosterAuthors?: Set<string>): TeamLogMessage[] {
  const latestStopByTrack = new Map<string, TeamLogMessage>();
  for (const m of teamLog) {
    const tags = m.tags ?? [];
    if (!tags.includes("track-stopped") && !tags.includes("build-stopped")) continue;
    const trackId = trackFromTags(tags);
    if (!trackId) continue;
    const existing = latestStopByTrack.get(trackId);
    if (!existing || (m.sequence ?? 0) > (existing.sequence ?? 0)) latestStopByTrack.set(trackId, m);
  }
  const results: TeamLogMessage[] = [];
  for (const [trackId, stopMsg] of latestStopByTrack) {
    const stopSeq = stopMsg.sequence ?? 0;
    const acked = teamLog.some(m => {
      const tags = m.tags ?? [];
      if ((m.sequence ?? 0) <= stopSeq) return false;
      if (!tags.includes(`track:${trackId}`)) return false;
      // action:dismiss-stopped ("no longer needed") clears the card like a re-queue
      // does, but the scheduler ignores it, so nothing is dispatched.
      if (!tags.includes("action:requeue") && !tags.includes("action:requeue-with-note") && !tags.includes("action:dismiss-stopped")) return false;
      if (humanRosterAuthors !== undefined && !humanRosterAuthors.has(m.author)) return false;
      return true;
    });
    if (!acked) results.push(stopMsg);
  }
  return results.sort(byRecency);
}
// relay-daemon's §3-C stale-agent-warning (packages/relay-daemon/src/poller.ts
// postStaleWarning, tags: stale-agent-warning, agent:{id}, relay,
// requires-response) fires after 20 minutes of silence from an agent. Per
// backlog spec seq #33307, the card should auto-resolve once the warned agent
// posts again. The daemon detects that (it already tracks per-session silence
// via lastStatusAt/lastSeenAt) and posts an explicit follow-up — see
// postStaleWarningResolved — tagged stale-agent-warning-resolved with
// reply-to:{warningSeq}, the same reply-to correlation every other card type
// in this tab uses. That, rather than scanning teamLog for the agent's own
// next post, is required because ActionsTab's teamLog feed is a Firestore
// query scoped to ACTIONS_TAB_TAGS — a plain build-complete/status/finding
// post from the agent (no tag in that list) never reaches this listener, so
// only a purpose-tagged reply the query already selects for can signal
// resolution here.
function collectStaleWarningResolvedSeqs(teamLog: TeamLogMessage[]): Set<number> {
  const set = new Set<number>();
  for (const m of teamLog) {
    if (!(m.tags ?? []).includes("stale-agent-warning-resolved")) continue;
    const seq = replyToSeq(m.tags);
    if (seq != null) set.add(seq);
  }
  return set;
}
function isStaleAgentWarningResolved(m: TeamLogMessage, resolvedSeqs: Set<number>): boolean {
  if (!(m.tags ?? []).includes("stale-agent-warning")) return false;
  return m.sequence != null && resolvedSeqs.has(m.sequence);
}
function byRecency(a: TeamLogMessage, b: TeamLogMessage): number {
  return (b.sequence ?? 0) - (a.sequence ?? 0);
}

// Platform alerts (Charter v88: the blocker tag is reserved for scheduler and
// daemon infrastructure alerts). Recognized by KIND tag, never by author name
// — class comes from the roster, not from a name (Charter "Team composition").
const PLATFORM_ALERT_TAGS = ["agent-died", "finding-gate-held", "watchdog", "scheduler-alert", "stale-agent-warning"];

// Scheduler watchdog alerts (adk-relay run_scheduler_watchdog_check) — rendered
// in their own "Scheduler Alerts" group and clearable with a plain
// finding-acknowledged reply, as before.
const RECOVERY_ONLY_TAGS = ["watchdog", "scheduler-alert"];
export function isRecoveryOnly(tags: string[] | undefined): boolean {
  return RECOVERY_ONLY_TAGS.some(t => (tags ?? []).includes(t));
}

// The daemon's fixed heading for a build-complete held at `blocked` because it
// carries no commit/branch/PR (poller.ts). If the daemon changes this heading
// the cards disappear (fail closed).
const SHIP_EVIDENCE_HEADING = "build-complete missing ship evidence";
function isShipEvidenceHold(m: TeamLogMessage): boolean {
  const tags = m.tags ?? [];
  if (!tags.includes("blocked") && !tags.includes("requires-response")) return false;
  return (m.content ?? "").includes(SHIP_EVIDENCE_HEADING);
}

// A blocker is only durably resolved once BOTH the resolution event
// (resolved) and the structured audit record (blocker-resolved) target its
// sequence — postResolution posts them as two writes, and requiring both keeps
// the card actionable if the second one fails (track:cc-actions-blocker-
// resolution-limbo-403). replyToSeqs: a closing reply can batch several seqs.
function collectResolvedBlockerSeqs(teamLog: TeamLogMessage[]): Set<number> {
  const resolvedSeqs = new Set<number>();
  const blockerResolvedSeqs = new Set<number>();
  for (const m of teamLog) {
    const tags = m.tags ?? [];
    if (tags.includes(TAG.RESOLVED)) for (const seq of replyToSeqs(tags)) resolvedSeqs.add(seq);
    if (tags.includes(TAG.BLOCKER_RESOLVED)) for (const seq of replyToSeqs(tags)) blockerResolvedSeqs.add(seq);
  }
  const both = new Set<number>();
  for (const seq of resolvedSeqs) if (blockerResolvedSeqs.has(seq)) both.add(seq);
  return both;
}

function collectAckedFindingSeqs(teamLog: TeamLogMessage[]): Set<number> {
  const set = new Set<number>();
  for (const m of teamLog) {
    const tags = m.tags ?? [];
    if (!tags.includes(TAG.FINDING_ACKNOWLEDGED)) continue;
    for (const seq of replyToSeqs(tags)) set.add(seq);
  }
  return set;
}

// Unresolved platform alerts: the five kind tags plus the daemon's ship-
// evidence hold for a track that is an item of the active sprint. One post
// counts once however many of its tags match.
export function collectPlatformAlerts(teamLog: TeamLogMessage[], activeSprint: ActiveSprintInfo): TeamLogMessage[] {
  const resolvedBlockerSeqs = collectResolvedBlockerSeqs(teamLog);
  const ackedFindingSeqs = collectAckedFindingSeqs(teamLog);
  const staleResolvedSeqs = collectStaleWarningResolvedSeqs(teamLog);
  const sprintTracks = new Set(((activeSprint.items as SprintItem[]) ?? []).map(i => i.title));
  return teamLog
    .filter(m => {
      const tags = m.tags ?? [];
      const isKind = PLATFORM_ALERT_TAGS.some(t => tags.includes(t));
      if (!isKind) {
        if (!isShipEvidenceHold(m)) return false;
        const track = trackFromTags(tags);
        if (!track || !sprintTracks.has(track)) return false;
      }
      if (m.sequence != null) {
        if (resolvedBlockerSeqs.has(m.sequence)) return false;
        if (isRecoveryOnly(tags) && ackedFindingSeqs.has(m.sequence)) return false;
      }
      if (isStaleAgentWarningResolved(m, staleResolvedSeqs)) return false;
      return true;
    })
    .sort(byRecency);
}

// Queued CRITICAL items on the active sprint with no critical-authorized post
// for their track (Charter v88 §5-B — every workspace is in Separate
// authorization today). HIGH needs nothing: "There is no pre-flight approval
// and no HIGH authorization."
export function collectAuthorizationNeeded(teamLog: TeamLogMessage[], activeSprint: ActiveSprintInfo): SprintItem[] {
  const authorized = new Set<string>();
  const dismissed = new Set<string>();
  const sprintTag = activeSprint.sprintId ? `sprint:${activeSprint.sprintId.replace("sprint-", "")}` : null;
  for (const m of teamLog) {
    const tags = m.tags ?? [];
    const track = trackFromTags(tags);
    if (!track) continue;
    // A post from another sprint says nothing about this one: a re-queued track
    // needs its own authorization. Posts with no sprint tag predate the tag and
    // still match by track alone.
    const postSprint = tags.find(t => t.startsWith("sprint:"));
    if (sprintTag && postSprint && postSprint !== sprintTag) continue;
    if (tags.includes("critical-authorized")) authorized.add(track);
    if (tags.includes("authorization-dismissed")) dismissed.add(track);
  }
  return ((activeSprint.items as SprintItem[]) ?? []).filter(item =>
    item.blast === "CRITICAL" &&
    (item.status ?? "queued") === "queued" &&
    !authorized.has(item.title) &&
    !dismissed.has(item.title),
  );
}

// Modal-queue items that ask something of a human: only the deterministic
// platform alert modal. confirm/preflight/peerReview/credential are retired.
// Reads `source` and `type`, never `author`: an item with no `source` (every
// @relay-produced item) is neither counted nor rendered.
export const PLATFORM_ALERT_SOURCES = ["scheduler", "daemon"];
export function selectActionModalItems(items: ModalQueueItem[]): ModalQueueItem[] {
  return items.filter(item => item.type === "alert" && PLATFORM_ALERT_SOURCES.includes(item.source ?? ""));
}

// The three kinds the tab keeps. Shared with ControlCenter/index.tsx so the
// badge reads the same derivation the tab renders.
export function computeTotalActions(
  teamLog: TeamLogMessage[],
  activeSprint: ActiveSprintInfo,
  humanRosterAuthors?: Set<string>,
): number {
  return (
    collectStoppedTracksNeedingRequeue(teamLog, humanRosterAuthors).length +
    collectAuthorizationNeeded(teamLog, activeSprint).length +
    collectPlatformAlerts(teamLog, activeSprint).length
  );
}

export function computeActionsBadge(
  teamLog: TeamLogMessage[],
  activeSprint: ActiveSprintInfo,
  modalQueueItems: ModalQueueItem[],
  humanRosterAuthors?: Set<string>,
): number {
  return computeTotalActions(teamLog, activeSprint, humanRosterAuthors) + selectActionModalItems(modalQueueItems).length;
}
